import {
    Avatar,
    AvatarFallback,
    AvatarImage,
    Switch,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationPrevious,
    PaginationNext,
    Badge,
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Label,
    Progress,
    Tooltip,
    TooltipTrigger,
    TooltipContent,
} from '@quadminds/mindkit';
import Lottie from 'lottie-react';
import { Search, ArrowUpDown, Mail, X, UserPlus } from 'lucide-react';
import animationData from '../../../../assets/json/loader.json';
import useUsersPage from '../../../../hooks/useUsersPage';
import FrameLayout from '../FrameLayout/FrameLayout';
import UserActions from '../UserActions/UserActions';
import UserConfig, { checkUserRoles, getRoleDisplayName } from '../UserConfig/UserConfig';

export function Users() {
    const {
        userInfo,
        currentOrganizationId,
        page,
        searchQuery,
        t,
        mindyApp,
        mindyAppRoles,
        mindyDocRoles,
        isLoading,
        users,
        totalPages,
        selectedConfigUser,
        setSelectedConfigUser,
        isInviteModalOpen,
        setIsInviteModalOpen,
        isEditModalOpen,
        setIsEditModalOpen,
        isDeleteModalOpen,
        setIsDeleteModalOpen,
        selectedUser,
        setSelectedUser,
        inviteEmails,
        setInviteEmails,
        currentEmailInput,
        setCurrentEmailInput,
        pendingUsers,
        maxLicenses,
        handlePageChange,
        statusFilter,
        roleFilter,
        handleSearchChange,
        handleToggleActive,
        handleStatusChange,
        handleRoleChange,
        isKnowledge,
        usedSlots,
        availableSlots,
        checkUserAplications,
        handleAddEmail,
        handleEmailInputKeyDown,
        handleRemoveEmail,
        handleInviteUsers,
        handleEditUser,
        handleDeleteUser,
        handleCancelInvite,
        handleResendInvitation,
        getResendCooldownSeconds,
        visiblePages,
        showActions,
    } = useUsersPage();

    return (
        <FrameLayout>
            {isKnowledge ? (
                <>
                    <div className="flex md:items-center justify-between max-md:flex-col">
                        <h1 className="font-poppins font-semibold text-xl max-md:mb-2">Usuarios</h1>
                        {/* Widget de usuarios disponibles */}
                        <div className="flex gap-6 py-1 px-3 border rounded-lg bg-gray-qm-50 dark:bg-gray-qm-900">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-semibold font-poppins mr-2">
                                    {t('settings.user.availableUsers', 'Disponibles')}:
                                </h3>
                                <span className="text-lg font-bold">
                                    <span className="text-primary dark:text-tertiary-qm-200">{availableSlots}</span> /{' '}
                                    {maxLicenses}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Progress value={(usedSlots / maxLicenses) * 100} className="h-2 flex-1 w-40" />
                                <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                    {usedSlots} {t('settings.user.used', 'Utilizados')}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className={`border rounded-lg shadow-sm `}>
                        <div className="p-4 border-b flex flex-col gap-4">
                            <div className="flex flex-col flex-wrap lg:flex-row gap-2 justify-between ">
                                <div className="flex items-center gap-2">
                                    <div className="relative flex-1 lg:max-w-80 min-w-48">
                                        <Search className="absolute left-2.5 top-3 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            type="search"
                                            placeholder={'Buscar Usuarios'}
                                            className="pl-8"
                                            value={searchQuery}
                                            onChange={handleSearchChange}
                                            data-testid="search-input"
                                        />
                                    </div>
                                    <div className="flex gap-2">
                                        <Select onValueChange={handleStatusChange} disabled={mindyApp === undefined}>
                                            <SelectTrigger className="w-[200px]">
                                                <SelectValue placeholder={t('settings.user.allLabel')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">{t('settings.user.all')}</SelectItem>
                                                <SelectItem value="active">{t('settings.user.active')}</SelectItem>
                                                <SelectItem value="inactive">{t('settings.user.inactive')}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <Select onValueChange={handleRoleChange} disabled={mindyDocRoles.length === 0}>
                                            <SelectTrigger className="w-[200px]">
                                                <SelectValue placeholder="Todos los Roles" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">{t('settings.user.all')}</SelectItem>
                                                {mindyDocRoles.map((role) => (
                                                    <SelectItem value={role.id} key={role.id}>
                                                        {t(role.translationKey, role.displayName)}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <Button onClick={() => setIsInviteModalOpen(true)} className="flex items-center gap-2">
                                    <UserPlus className="h-4 w-4" />
                                    {t('settings.user.inviteUsers', 'Invitar Usuarios')}
                                </Button>
                            </div>
                        </div>
                        <div>
                            {isLoading ? (
                                <div className="flex justify-center items-center h-96">
                                    <Lottie className="h-28 w-36" animationData={animationData} loop={true} />
                                </div>
                            ) : (
                                <>
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="border-gray-200 dark:border-gray-700 hover:bg-gray-100/50 dark:hover:bg-gray-800/50">
                                                <TableHead className="text-gray-700 dark:text-gray-300 font-medium w-full">
                                                    <div className="flex items-center gap-2">
                                                        {t('settings.user.name')}
                                                        <ArrowUpDown className="h-4 w-4" />
                                                    </div>
                                                </TableHead>
                                                <TableHead className="text-gray-700 dark:text-gray-300 font-medium text-center max-w-[155px]">
                                                    <p>{t('settings.user.active')}</p>
                                                </TableHead>
                                                <TableHead className="text-gray-700 dark:text-gray-300 font-medium">
                                                    <div className="flex items-center gap-2">
                                                        {t('settings.user.status', 'Estado')}
                                                        <ArrowUpDown className="h-4 w-4" />
                                                    </div>
                                                </TableHead>
                                                <TableHead className="text-gray-700 dark:text-gray-300 font-medium">
                                                    <div className="flex items-center gap-2">
                                                        {t('settings.user.role')}
                                                        <ArrowUpDown className="h-4 w-4" />
                                                    </div>
                                                </TableHead>
                                                <TableHead className="text-gray-700 dark:text-gray-300 font-medium w-16">
                                                    {t('settings.user.actions', 'Acciones')}
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {/* Usuarios activos */}
                                            {users
                                                .filter(
                                                    (u) =>
                                                        (isKnowledge || u.id !== userInfo?.id) &&
                                                        checkUserAplications(u, currentOrganizationId),
                                                )
                                                .map((user) => {
                                                    const gotMindyApp = true;
                                                    const userRole = checkUserRoles(user, currentOrganizationId);
                                                    const isCurrentUser = !!isKnowledge && user.id === userInfo?.id;

                                                    return (
                                                        <TableRow
                                                            key={user.id}
                                                            className="border-gray-200 dark:border-gray-700 hover:bg-gray-100/50 dark:hover:bg-gray-800/50"
                                                        >
                                                            <TableCell className="align-middle">
                                                                <div className="flex items-center gap-3">
                                                                    <Avatar className="h-8 w-8">
                                                                        <AvatarImage
                                                                            src={user.avatar || '/placeholder.svg'}
                                                                            alt={user.firstName}
                                                                        />
                                                                        <AvatarFallback className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                                                            {user.firstName
                                                                                .trim()
                                                                                .split(' ')
                                                                                .map((n) => n[0])
                                                                                .join('')}
                                                                        </AvatarFallback>
                                                                    </Avatar>
                                                                    <div>
                                                                        <div className="font-medium text-gray-900 dark:text-white">{`${
                                                                            user.firstName || ''
                                                                        }  ${user.lastName || ''}`}</div>
                                                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                            {user.email || ''}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="text-center align-middle max-w-[155px]">
                                                                <Switch
                                                                    checked={gotMindyApp}
                                                                    onCheckedChange={(checked) =>
                                                                        handleToggleActive(user.id, checked)
                                                                    }
                                                                    className="scale-75 inline-block"
                                                                    disabled={!showActions || isCurrentUser}
                                                                />
                                                            </TableCell>
                                                            <TableCell className="align-middle">
                                                                <Badge
                                                                    variant="default"
                                                                    className="bg-primary text-white"
                                                                >
                                                                    {t('settings.user.active', 'Activo')}
                                                                </Badge>
                                                            </TableCell>
                                                            <TableCell className="align-middle">
                                                                {getRoleDisplayName(userRole, t)}
                                                            </TableCell>
                                                            {showActions && (
                                                                <TableCell className="align-middle">
                                                                    {!isCurrentUser && (
                                                                        <>
                                                                            <UserActions
                                                                                user={user}
                                                                                onDelete={() => {
                                                                                    setSelectedUser(user);
                                                                                    setIsDeleteModalOpen(true);
                                                                                }}
                                                                                onOpenSettings={() =>
                                                                                    setSelectedConfigUser(user.id)
                                                                                }
                                                                            />
                                                                            <UserConfig
                                                                                user={user}
                                                                                isOpen={user.id === selectedConfigUser}
                                                                                handleOpen={(open: boolean) =>
                                                                                    setSelectedConfigUser(
                                                                                        open ? user.id : '',
                                                                                    )
                                                                                }
                                                                                currentOrganizationId={
                                                                                    currentOrganizationId
                                                                                }
                                                                                currentApplication={mindyApp}
                                                                                isKnowledge={!!isKnowledge}
                                                                            />
                                                                        </>
                                                                    )}
                                                                </TableCell>
                                                            )}
                                                        </TableRow>
                                                    );
                                                })}

                                            {/* Usuarios inactivos */}
                                            {users
                                                .filter(
                                                    (u) =>
                                                        (isKnowledge || u.id !== userInfo?.id) &&
                                                        !checkUserAplications(u, currentOrganizationId),
                                                )
                                                .map((user) => {
                                                    const gotMindyApp = false;
                                                    const userRole = checkUserRoles(user, currentOrganizationId);
                                                    const isCurrentUser = !!isKnowledge && user.id === userInfo?.id;

                                                    return (
                                                        <TableRow
                                                            key={user.id}
                                                            className="border-gray-200 dark:border-gray-700 hover:bg-gray-100/50 dark:hover:bg-gray-800/50"
                                                        >
                                                            <TableCell className="align-middle">
                                                                <div className="flex items-center gap-3">
                                                                    <Avatar className="h-8 w-8">
                                                                        <AvatarImage
                                                                            src={user.avatar || '/placeholder.svg'}
                                                                            alt={user.firstName}
                                                                        />
                                                                        <AvatarFallback className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                                                            {user.firstName
                                                                                .trim()
                                                                                .split(' ')
                                                                                .map((n) => n[0])
                                                                                .join('')}
                                                                        </AvatarFallback>
                                                                    </Avatar>
                                                                    <div>
                                                                        <div className="font-medium text-gray-900 dark:text-white">{`${
                                                                            user.firstName || ''
                                                                        }  ${user.lastName || ''}`}</div>
                                                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                            {user.email || ''}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="text-center align-middle max-w-[155px]">
                                                                <Switch
                                                                    checked={gotMindyApp}
                                                                    onCheckedChange={(checked) =>
                                                                        handleToggleActive(user.id, checked)
                                                                    }
                                                                    className="scale-75 inline-block"
                                                                    disabled={!showActions || isCurrentUser}
                                                                />
                                                            </TableCell>
                                                            <TableCell className="align-middle">
                                                                <Badge variant="secondary">
                                                                    {t('settings.user.inactive', 'Inactivo')}
                                                                </Badge>
                                                            </TableCell>
                                                            <TableCell className="align-middle">
                                                                {getRoleDisplayName(userRole, t)}
                                                            </TableCell>
                                                            {showActions && (
                                                                <TableCell className="align-middle">
                                                                    {!isCurrentUser && (
                                                                        <>
                                                                            <UserActions
                                                                                user={user}
                                                                                onDelete={() => {
                                                                                    setSelectedUser(user);
                                                                                    setIsDeleteModalOpen(true);
                                                                                }}
                                                                                onOpenSettings={() =>
                                                                                    setSelectedConfigUser(user.id)
                                                                                }
                                                                            />
                                                                            <UserConfig
                                                                                user={user}
                                                                                isOpen={user.id === selectedConfigUser}
                                                                                handleOpen={(open: boolean) =>
                                                                                    setSelectedConfigUser(
                                                                                        open ? user.id : '',
                                                                                    )
                                                                                }
                                                                                currentOrganizationId={
                                                                                    currentOrganizationId
                                                                                }
                                                                                currentApplication={mindyApp}
                                                                                isKnowledge={!!isKnowledge}
                                                                            />
                                                                        </>
                                                                    )}
                                                                </TableCell>
                                                            )}
                                                        </TableRow>
                                                    );
                                                })}

                                            {/* Invitaciones pendientes */}
                                            {!searchQuery &&
                                                (!statusFilter || statusFilter === 'all') &&
                                                (!roleFilter || roleFilter === 'all') &&
                                                pendingUsers.map((user) => {
                                                    return (
                                                        <TableRow
                                                            key={user.id}
                                                            className="border-gray-200 dark:border-gray-700 hover:bg-gray-100/50 dark:hover:bg-gray-800/50"
                                                        >
                                                            <TableCell className="align-middle">
                                                                <div className="flex items-center gap-3">
                                                                    <Avatar className="h-8 w-8">
                                                                        <AvatarFallback className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                                                            {user.email.charAt(0).toUpperCase()}
                                                                        </AvatarFallback>
                                                                    </Avatar>
                                                                    <div>
                                                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                            {user.email}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="text-center align-middle"></TableCell>
                                                            <TableCell className="align-middle">
                                                                <div className="flex items-center gap-2">
                                                                    {user.status === 'redeemed' ? (
                                                                        <Badge
                                                                            variant="default"
                                                                            className="bg-green-600 text-white"
                                                                        >
                                                                            {t('settings.user.redeemed', 'Aceptada')}
                                                                        </Badge>
                                                                    ) : (
                                                                        <>
                                                                            <Badge
                                                                                variant="outline"
                                                                                className="border-orange-500 text-orange-500"
                                                                            >
                                                                                {t(
                                                                                    'settings.user.pending',
                                                                                    'Pendiente',
                                                                                )}
                                                                            </Badge>
                                                                            <Tooltip>
                                                                                <TooltipTrigger asChild>
                                                                                    <Button
                                                                                        variant="ghost"
                                                                                        size="sm"
                                                                                        disabled={
                                                                                            getResendCooldownSeconds(
                                                                                                user.email,
                                                                                            ) > 0
                                                                                        }
                                                                                        onClick={() =>
                                                                                            handleResendInvitation(
                                                                                                user.email,
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        <Mail className="h-4 w-4" />
                                                                                    </Button>
                                                                                </TooltipTrigger>
                                                                                <TooltipContent>
                                                                                    {t(
                                                                                        'settings.user.resendInvitation',
                                                                                        'Reenviar Invitación',
                                                                                    )}
                                                                                </TooltipContent>
                                                                            </Tooltip>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="align-middle text-gray-500">
                                                                -
                                                            </TableCell>
                                                            <TableCell className="align-middle">
                                                                <UserActions
                                                                    user={user}
                                                                    onDelete={(u) => handleCancelInvite(u.id)}
                                                                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                                                                    onOpenSettings={() => {}}
                                                                    hideSettings={true}
                                                                    deleteLabel={t(
                                                                        'settings.user.cancelInvitation',
                                                                        'Cancelar invitación',
                                                                    )}
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                        </TableBody>
                                    </Table>
                                    <Pagination data-testid="users-pagination" className="mt-4 pb-4 pr-4 justify-end">
                                        <PaginationContent>
                                            <PaginationPrevious
                                                label={t('settings.user.previous')}
                                                onClick={() => handlePageChange(page > 1 ? page - 1 : 1)}
                                            />
                                            {visiblePages.map((pageNumber) => (
                                                <PaginationItem key={pageNumber}>
                                                    <PaginationLink
                                                        isActive={page === pageNumber}
                                                        onClick={() => handlePageChange(pageNumber)}
                                                    >
                                                        {pageNumber}
                                                    </PaginationLink>
                                                </PaginationItem>
                                            ))}
                                            <PaginationNext
                                                label={t('settings.user.next')}
                                                onClick={() => handlePageChange(page < totalPages ? page + 1 : page)}
                                            />
                                        </PaginationContent>
                                    </Pagination>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Modal de Invitación */}
                    <Dialog
                        open={isInviteModalOpen}
                        onOpenChange={(open) => {
                            setIsInviteModalOpen(open);
                            if (!open) {
                                setInviteEmails([]);
                                setCurrentEmailInput('');
                            }
                        }}
                    >
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle className="text-xl font-poppins font-semibold">
                                    {t('settings.user.inviteUsers', 'Invitar Usuarios')}
                                </DialogTitle>
                                <DialogDescription className="text-muted-foreground">
                                    {t(
                                        'settings.user.inviteDescription',
                                        'Ingresá una o más direcciones de correo y enviá la invitación.',
                                    )}
                                    <br />
                                    {t(
                                        'settings.user.inviteEmailNote',
                                        'Cada persona recibirá un email con un enlace para registrarse y crear su contraseña.',
                                    )}
                                </DialogDescription>
                                <div className="border-b mt-4" />
                            </DialogHeader>
                            <div className="space-y-4 pb-4">
                                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                                    <p className="text-sm text-blue-700 dark:text-blue-300">
                                        {t(
                                            'settings.user.availableSlots',
                                            `Usuarios disponibles: ${
                                                availableSlots - inviteEmails.length
                                            } de ${maxLicenses}`,
                                        )}
                                    </p>
                                </div>
                                <div>
                                    <Label htmlFor="emails">{t('settings.user.users', 'Usuarios')}</Label>
                                    <div className="mt-2 min-h-[32px] w-full rounded-md border border-input bg-background px-2 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                                        <div className="flex flex-wrap gap-2">
                                            {inviteEmails.map((email, index) => (
                                                <Badge key={index} variant="secondary" className="gap-1 py-2">
                                                    {email}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveEmail(email)}
                                                        className="ml-1 rounded-sm opacity-70 hover:opacity-100 transition-opacity"
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                </Badge>
                                            ))}
                                            <Input
                                                id="emails"
                                                placeholder={inviteEmails.length === 0 ? 'nombre@empresa.com' : ''}
                                                value={currentEmailInput}
                                                onChange={(e) => setCurrentEmailInput(e.target.value)}
                                                onKeyDown={handleEmailInputKeyDown}
                                                onBlur={() => {
                                                    if (currentEmailInput.trim()) {
                                                        handleAddEmail(currentEmailInput);
                                                    }
                                                }}
                                                className="flex-1 border-0 p-0 focus-visible:ring-0 focus-visible:ring-offset-0 min-w-[200px]"
                                                disabled={availableSlots - inviteEmails.length <= 0}
                                            />
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {t(
                                            'settings.user.separateEmails',
                                            'Ingresá direcciones de email válidas y presioná Enter, Tab o coma para agregarlas. Podés eliminar con Backspace.',
                                        )}
                                    </p>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setIsInviteModalOpen(false);
                                        setInviteEmails([]);
                                        setCurrentEmailInput('');
                                    }}
                                >
                                    {t('common.cancel', 'Cancelar')}
                                </Button>
                                <Button onClick={handleInviteUsers} disabled={inviteEmails.length === 0}>
                                    {t('settings.user.invite', 'Invitar')}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>

                    {/* Modal de Edición */}
                    <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle className="text-xl font-poppins font-semibold">
                                    {t('settings.user.editUser', 'Editar Usuario')}
                                </DialogTitle>
                                <div className="border-b mt-4" />
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                                <div>
                                    <Label htmlFor="firstName">{t('settings.user.firstName', 'Nombre')}</Label>
                                    <Input
                                        id="firstName"
                                        defaultValue={selectedUser?.firstName || ''}
                                        className="mt-2"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="lastName">{t('settings.user.lastName', 'Apellido')}</Label>
                                    <Input id="lastName" defaultValue={selectedUser?.lastName || ''} className="mt-2" />
                                </div>
                                <div>
                                    <Label htmlFor="email">{t('settings.user.email', 'Email')}</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        defaultValue={selectedUser?.email || ''}
                                        className="mt-2"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="role">{t('settings.user.role', 'Rol')}</Label>
                                    <Select
                                        defaultValue={
                                            selectedUser ? checkUserRoles(selectedUser, currentOrganizationId) : ''
                                        }
                                    >
                                        <SelectTrigger className="mt-2">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {mindyAppRoles.map((role) => (
                                                <SelectItem value={role.id} key={role.id}>
                                                    {t(role.translationKey, role.displayName)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Switch
                                        id="active"
                                        defaultChecked={
                                            selectedUser
                                                ? checkUserAplications(selectedUser, currentOrganizationId)
                                                : false
                                        }
                                    />
                                    <Label htmlFor="active">{t('settings.user.active', 'Activo')}</Label>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
                                    {t('common.cancel', 'Cancelar')}
                                </Button>
                                <Button onClick={handleEditUser}>{t('common.save', 'Guardar')}</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>

                    {/* Modal de Confirmación de Eliminación */}
                    <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle className="text-xl font-poppins font-semibold">
                                    {t('settings.user.deleteUser', 'Eliminar Usuario')}
                                </DialogTitle>
                                <div className="border-b mt-4" />
                                <DialogDescription className="text-muted-foreground pt-4">
                                    {t(
                                        'settings.user.deleteConfirmation',
                                        'Vas a eliminar a este usuario de la organización y perderá el acceso a la plataforma. Esta acción no se puede deshacer.',
                                    )}
                                </DialogDescription>
                            </DialogHeader>
                            <div className="py-4">
                                <p className="text-sm">
                                    <strong>
                                        {selectedUser?.firstName} {selectedUser?.lastName}
                                    </strong>
                                    <br />
                                    {selectedUser?.email}
                                </p>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>
                                    {t('common.cancel', 'Cancelar')}
                                </Button>
                                <Button variant="destructive" onClick={handleDeleteUser}>
                                    {t('common.delete', 'Eliminar')}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </>
            ) : (
                <div className={`border rounded-lg shadow-sm `}>
                    <div className="p-4 border-b flex flex-col gap-4">
                        <div className="flex flex-col flex-wrap lg:flex-row gap-2 justify-between ">
                            <div className="flex items-center gap-2">
                                <div className="relative flex-1 lg:max-w-80 min-w-48">
                                    <Search className="absolute left-2.5 top-3 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        type="search"
                                        placeholder={'Buscar Usuarios'}
                                        className="pl-8"
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                        data-testid="search-input"
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <Select onValueChange={handleStatusChange} disabled={mindyApp === undefined}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder={t('settings.user.allLabel')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">{t('settings.user.all')}</SelectItem>{' '}
                                            {/* Opción en blanco para limpiar el filtro */}
                                            <SelectItem value="active">{t('settings.user.active')}</SelectItem>
                                            <SelectItem value="inactive">{t('settings.user.inactive')}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Select onValueChange={handleRoleChange} disabled={mindyAppRoles.length === 0}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder="Todos los Roles" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">{t('settings.user.all')}</SelectItem>{' '}
                                            {/* Opción en blanco para limpiar el filtro */}
                                            {mindyAppRoles.map((role) => (
                                                <SelectItem value={role.id} key={role.id}>
                                                    {t(role.translationKey, role.displayName)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div>
                        {isLoading ? (
                            <div className="flex justify-center items-center h-96">
                                <Lottie className="h-28 w-36" animationData={animationData} loop={true} />
                            </div>
                        ) : (
                            <>
                                <Table>
                                    <TableHeader>
                                        <TableRow className="border-gray-700 hover:bg-gray-800/50">
                                            <TableHead className="text-gray-300 font-medium w-full">
                                                <div className="flex items-center gap-2">
                                                    {t('settings.user.name')}
                                                    <ArrowUpDown className="h-4 w-4" />
                                                </div>
                                            </TableHead>
                                            <TableHead className="text-gray-300 font-medium text-center max-w-[155px]">
                                                <p>{t('settings.user.active')}</p>
                                            </TableHead>
                                            <TableHead>{t('settings.user.role')}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {/* Usuarios activos */}
                                        {users
                                            .filter(
                                                (u) =>
                                                    (isKnowledge || u.id !== userInfo?.id) &&
                                                    checkUserAplications(u, currentOrganizationId),
                                            )
                                            .map((user) => {
                                                const userRole = checkUserRoles(user, currentOrganizationId);
                                                const isCurrentUser = !!isKnowledge && user.id === userInfo?.id;
                                                return (
                                                    <TableRow
                                                        key={user.id}
                                                        className="border-gray-700 hover:bg-gray-800/50"
                                                    >
                                                        <TableCell className="align-middle">
                                                            <div className="flex items-center gap-3">
                                                                <Avatar className="h-8 w-8">
                                                                    <AvatarImage
                                                                        src={user.avatar || '/placeholder.svg'}
                                                                        alt={user.firstName}
                                                                    />
                                                                    <AvatarFallback className="bg-gray-700 text-gray-300">
                                                                        {user.firstName
                                                                            .trim()
                                                                            .split(' ')
                                                                            .map((n) => n[0])
                                                                            .join('')}
                                                                    </AvatarFallback>
                                                                </Avatar>
                                                                <div>
                                                                    <div className="font-medium text-white">{`${
                                                                        user.firstName || ''
                                                                    }  ${user.lastName || ''}`}</div>
                                                                    <div className="text-sm text-gray-400">
                                                                        {user.email || ''}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-center align-middle max-w-[155px]">
                                                            <Switch
                                                                checked={true}
                                                                onCheckedChange={(checked) =>
                                                                    handleToggleActive(user.id, checked)
                                                                }
                                                                className="scale-75 inline-block"
                                                                disabled={!showActions || isCurrentUser}
                                                            />
                                                        </TableCell>
                                                        <TableCell className="align-middle">
                                                            {getRoleDisplayName(userRole, t)}
                                                        </TableCell>
                                                        {showActions && (
                                                            <TableCell className="align-middle">
                                                                {!isCurrentUser && (
                                                                    <>
                                                                        <UserActions
                                                                            user={user}
                                                                            onDelete={() => null}
                                                                            onOpenSettings={() =>
                                                                                setSelectedConfigUser(user.id)
                                                                            }
                                                                            hideDelete={true}
                                                                        />
                                                                        <UserConfig
                                                                            user={user}
                                                                            isOpen={user.id === selectedConfigUser}
                                                                            handleOpen={(open: boolean) =>
                                                                                setSelectedConfigUser(
                                                                                    open ? user.id : '',
                                                                                )
                                                                            }
                                                                            currentOrganizationId={
                                                                                currentOrganizationId
                                                                            }
                                                                            currentApplication={mindyApp}
                                                                            isKnowledge={!!isKnowledge}
                                                                        />
                                                                    </>
                                                                )}
                                                            </TableCell>
                                                        )}
                                                    </TableRow>
                                                );
                                            })}

                                        {/* Usuarios inactivos */}
                                        {users
                                            .filter(
                                                (u) =>
                                                    (isKnowledge || u.id !== userInfo?.id) &&
                                                    !checkUserAplications(u, currentOrganizationId),
                                            )
                                            .map((user) => {
                                                const userRole = checkUserRoles(user, currentOrganizationId);
                                                const isCurrentUser = !!isKnowledge && user.id === userInfo?.id;
                                                return (
                                                    <TableRow
                                                        key={user.id}
                                                        className="border-gray-700 hover:bg-gray-800/50"
                                                    >
                                                        <TableCell className="align-middle">
                                                            <div className="flex items-center gap-3">
                                                                <Avatar className="h-8 w-8">
                                                                    <AvatarImage
                                                                        src={user.avatar || '/placeholder.svg'}
                                                                        alt={user.firstName}
                                                                    />
                                                                    <AvatarFallback className="bg-gray-700 text-gray-300">
                                                                        {user.firstName
                                                                            .trim()
                                                                            .split(' ')
                                                                            .map((n) => n[0])
                                                                            .join('')}
                                                                    </AvatarFallback>
                                                                </Avatar>
                                                                <div>
                                                                    <div className="font-medium text-white">{`${
                                                                        user.firstName || ''
                                                                    }  ${user.lastName || ''}`}</div>
                                                                    <div className="text-sm text-gray-400">
                                                                        {user.email || ''}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-center align-middle max-w-[155px]">
                                                            <Switch
                                                                checked={false}
                                                                onCheckedChange={(checked) =>
                                                                    handleToggleActive(user.id, checked)
                                                                }
                                                                className="scale-75 inline-block"
                                                                disabled={!showActions || isCurrentUser}
                                                            />
                                                        </TableCell>
                                                        <TableCell className="align-middle">
                                                            {getRoleDisplayName(userRole, t)}
                                                        </TableCell>
                                                        {showActions && (
                                                            <TableCell className="align-middle">
                                                                {!isCurrentUser && (
                                                                    <>
                                                                        <UserActions
                                                                            user={user}
                                                                            onDelete={() => null}
                                                                            onOpenSettings={() =>
                                                                                setSelectedConfigUser(user.id)
                                                                            }
                                                                            hideDelete={true}
                                                                        />
                                                                        <UserConfig
                                                                            user={user}
                                                                            isOpen={user.id === selectedConfigUser}
                                                                            handleOpen={(open: boolean) =>
                                                                                setSelectedConfigUser(
                                                                                    open ? user.id : '',
                                                                                )
                                                                            }
                                                                            currentOrganizationId={
                                                                                currentOrganizationId
                                                                            }
                                                                            currentApplication={mindyApp}
                                                                            isKnowledge={!!isKnowledge}
                                                                        />
                                                                    </>
                                                                )}
                                                            </TableCell>
                                                        )}
                                                    </TableRow>
                                                );
                                            })}
                                    </TableBody>
                                </Table>
                                <Pagination data-testid="users-pagination" className="mt-4">
                                    <PaginationContent>
                                        <PaginationPrevious
                                            label={t('settings.user.previous')}
                                            onClick={() => handlePageChange(page > 1 ? page - 1 : 1)}
                                        />
                                        {visiblePages.map((pageNumber) => (
                                            <PaginationItem key={pageNumber}>
                                                <PaginationLink
                                                    isActive={page === pageNumber}
                                                    onClick={() => handlePageChange(pageNumber)}
                                                >
                                                    {pageNumber}
                                                </PaginationLink>
                                            </PaginationItem>
                                        ))}
                                        <PaginationNext
                                            label={t('settings.user.next')}
                                            onClick={() => handlePageChange(page < totalPages ? page + 1 : page)}
                                        />
                                    </PaginationContent>
                                </Pagination>
                            </>
                        )}
                    </div>
                </div>
            )}
        </FrameLayout>
    );
}

export default Users;
